import React, { useState, useEffect, ReactElement } from "react";
import SupportIcon from "../icons/SupportIcon";
import { useBrand } from "../../theme/BrandProvider";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import useLogout from '../../hooks/useLogout';
import LockIcon from "../icons/LockIcon";

interface ResponsiveHeaderProps {
  title?: string;
  showSupport?: boolean;
  onSupportClick?: () => void;
  className?: string;
}

const ResponsiveHeader: React.FC<ResponsiveHeaderProps> = ({
  title = "Agent Onboarding Portal",
  showSupport = true,
  onSupportClick,
  className = "",
}) => {
  const brand = useBrand();
  const isWarburg = brand.key === 'warburg';
  const [isMobile, setIsMobile] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const performLogout = useLogout();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleSupportClick = () => {
    if (onSupportClick) return onSupportClick();
    // Default behavior: navigate to support route
    navigate('/support');
  };

  const handleLogout = () => { performLogout(); };

  // Helper to safely clone a logo element adding className/style only if props exist
  interface LogoLikeProps { className?: string; style?: React.CSSProperties; width?: number; height?: number; color?: string; variant?: 'mobile' | 'desktop' }
  const renderLogo = (variant: 'mobile' | 'desktop') => {
    const base = brand.logo;
    if (!React.isValidElement(base)) return base;
    const el = base as ReactElement<LogoLikeProps>;
    const sharedClass = "h-11 object-contain";
    const themeColors: Record<string, unknown> = (brand.theme as unknown as { colors?: Record<string, unknown> }).colors || {};
    
    // Compass-specific: black logo on white mobile header, white logo on black desktop header
    const isCompass = brand.name === 'Compass';
    let logoColor: string | undefined;
    if (isCompass) {
      logoColor = variant === 'mobile' ? (themeColors.black as string | undefined) : (themeColors.white as string | undefined);
    } else {
      logoColor = variant === 'mobile' || isWarburg
        ? (themeColors.logo as string | undefined)
        : (themeColors.white as string | undefined);
    }
    logoColor = logoColor ?? el.props.color;
    
    const baseStyle: React.CSSProperties = el.props.style ?? {};
    const style: React.CSSProperties = isWarburg
      ? {
          ...baseStyle,
          width: variant === 'mobile' ? 44 : 90,
          height: variant === 'mobile' ? 44 : 30,
          color: logoColor,
          ...(variant === 'mobile' ? { backgroundColor: '#fff' } : { fill: logoColor }),
        }
      : variant === 'mobile'
        ? { ...baseStyle, color: logoColor }
        : { ...baseStyle, width: 219, height: 30, color: logoColor };
    return React.cloneElement(el, { className: sharedClass, style, ...(isWarburg ? { variant } : {}) });
  };

  if (isMobile) {
    return (
      <header
        className={`bg-white px-4 md:px-8 h-[60px] sticky top-0 z-50 ${className}`}
        style={{ borderBottom: "1px solid #00000080" }}
      >
        <div className={`flex justify-between items-center h-full`}>
          {/* Mobile Logo */}
          <div className="">{renderLogo('mobile')}</div>
          <div className="flex items-center gap-3">
            {isAuthenticated && (
              <button
                type="button"
                onClick={handleLogout}
                aria-label="Logout"
                className="text-[#141414]"
                title="Logout"
              >
                <LockIcon />
              </button>
            )}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      className={`px-4 md:px-8 sticky top-0 z-50 h-[60px] ${className}`}
      style={{ background: brand.theme.colors.header }}
    >
      <div className="flex justify-between items-center h-full">
      <div className="flex items-center gap-3">
        {renderLogo('desktop')}
        <div className={`w-px h-8 ${isWarburg ? 'bg-black' : 'bg-white'} opacity-50`} />
        <h1 className={`text-lg md:text-xl font-semibold ${isWarburg ? 'text-black' : 'text-white'}`}>
        {title}
        </h1>
      </div>
      <div className="flex items-center gap-6">
        {showSupport && (
        <button
          type="button"
          onClick={handleSupportClick}
          className={`flex items-center cursor-pointer focus:outline-none focus-visible:ring-2 ${isWarburg ? 'focus-visible:ring-black/60' : 'focus-visible:ring-white/60'} rounded`}
          aria-label="Support"
          title="Support"
        >
          <SupportIcon
          width={24}
          height={24}
          color={isWarburg ? '#000000' : '#ffffff'}
          className=""
          />
          <span className="w-2" />
          <span className={isWarburg ? 'text-black' : 'text-white'}>Support</span>
        </button>
        )}
        {isAuthenticated && (
        <button
          type="button"
          onClick={handleLogout}
          aria-label="Logout"
          className={isWarburg ? 'text-black' : 'text-white'}
          title="Logout"
        >
          <LockIcon />
        </button>
        )}
      </div>
      </div>
    </header>
  );
};

export default ResponsiveHeader;